import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Divider, List, Message, Modal } from '@meitu/saas-ui';
import './material.less';
import classnames from 'classnames';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { getImgInfoAsync, imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { MediaType } from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui';
import ImageUpload, { saveSvgParams } from '@meitu/saas-editor-ui/es/components/image-upload';
import IconFont from '@meitu/xdesign-icon-font';
import { EVENT_FILES_UPLOAD, events } from '@/events-bus';
import useAddUploadLayer from '@/hooks/use-add-upload-layer';
import { useDragLayer } from '@/hooks/use-drag-layer';
import { editor } from '@/instance';
import { deleteMyPicture, fetchMyPicture, saveMyPicture } from '@/services/record';
import { useStoreState } from '@/store';
import { getMediaType } from '@/utils/layer';
import useFilesUpload, { saveMyPictureByUrl } from '../hooks/use-files-upload';

interface ImgOnloadProp {
    width: number;
    height: number;
    svgInfo?: saveSvgParams;
}
interface imgDataType {
    url: string;
    name?: string;
    id: string;
    blobUrl?: string; // 本地预览的图片地址
    width: number;
    height: number;
    status?: string;
    file?: any;
    ext?: string;
}

const WidthCard = 120;
const PAGE_COUNT = 30;

function Mine() {
    const [imageList, setImageList] = useState<imgDataType[]>([]); // 图片列表
    const imageRef = useRef<imgDataType[]>([]);
    const uploadRef = useRef<HTMLDivElement | null>(null);
    const [loading, setLoading] = useState<boolean>(false); //下拉加载
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [curPage, setCurPage] = useState<number>(1);
    const dragLayerHelper = useDragLayer();
    const { filesUpload } = useFilesUpload();
    const currentType = useStoreState((state) => state.collapseModel.currentType);
    const [checkedMap, setCheckedMap] = useState<{
        [key: number]: boolean;
    }>({});
    const isAddMaterialRef = useRef(false);

    const { handleAddUploadLayer } = useAddUploadLayer();

    const baseInfo = useRef({
        targetId: '',
        currentUrl: '',
        isRelease: false,
    });

    useEffect(() => {
        if (currentType === CollapseType.MINE_MENU) {
            fetchList();
        }
    }, [currentType]);

    useEffect(() => {
        editor.contextMenu.onAddImage(() => {
            if (!uploadRef.current) return;
            const input = uploadRef.current.querySelector('input[type="file"]') as HTMLInputElement;
            if (input) {
                isAddMaterialRef.current = true;
                input.click(); // 触发文件选择弹窗
            }
        });
    }, []);

    const deleteIndexMaterial = async () => {
        Modal.confirm({
            title: '删除提示',
            content: '删除后将无法重新找回，请谨慎操作',
            okText: '确认删除',
            cancelText: '取消',
            centered: true,
            closable: true,
            onOk: async () => {
                const ids = Object.keys(checkedMap).filter((key) => checkedMap[+key]);
                await deleteMyPicture({ id: ids.join(',') });
                setImageList(imageRef.current.filter((v) => !ids.includes(`${v.id}`)));
                setCheckedMap({});
                Message.success('删除成功');
            },
            onCancel: () => {},
        });
    };

    const fetchList = async (page: number = 1) => {
        try {
            setLoading(true);
            const { lists, total } = await fetchMyPicture({
                page,
                count: PAGE_COUNT,
                meta_type: 1,
            });

            if (lists) {
                setImageList(imageRef.current.concat(lists));
                setHasMore(PAGE_COUNT * page < total);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const nextPageRequest = () => {
        if (loading || !hasMore) return;
        setCurPage(curPage + 1);
        fetchList(curPage + 1);
    };

    const uploadImgProp = {
        convertToWebp: false,
        maxCount: 10,
        children: (
            <Button className="c-text-collapse__btn" type="primary" data-blue>
                <IconFont name="Plus" fontSize={12} />
                添加素材
            </Button>
        ),
        disabled: false,
        onChange(file: any) {
            const blobUrl = (file?.originFileObj && window.URL.createObjectURL(file.originFileObj)) || '';
            // 局部数据，不存放store
            const list = [
                {
                    url: '',
                    name: file.name,
                    id: file.uid,
                    blobUrl,
                    width: 0,
                    height: 0,
                },
                ...imageRef.current,
            ];
            setImageList([...list]);
        },
        async onSuccess(url: string, options: ImgOnloadProp, uid: string) {
            // const imgInfo = (await getImgInfoAsync(url)) as ImgOnloadProp;
            // console.log(imgInfo, '>>imgInfo');
            const { isMediaLayer, mediaType } = getMediaType(url);

            const ext = JSON.stringify(isMediaLayer ? { isMediaLayer, mediaType } : options.svgInfo);

            // console.log('url===', options, ext, url);

            const { data } = await saveMyPicture({
                url,
                width: options.width,
                height: options.height,
                ext,
            });
            const id = data.response.id;
            if (!id) return;
            const index = imageRef.current.findIndex((v) => v.id === uid);
            if (imageRef.current.length === 0) {
                imageRef.current.push({
                    url,
                    id: id.toString(),
                    width: options.width,
                    height: options.height,
                    status: 'success',
                    ext,
                });
            } else {
                imageRef.current.splice(index, 1, {
                    url,
                    id: id.toString(),
                    width: options.width,
                    height: options.height,
                    status: 'success',
                    ext,
                });
            }
            const list = imageRef.current.concat();

            setImageList([...list]);
            // 存入 store 层数据 （目前没有在服务端存储）
            // setMaterialImages(list);
            Message.success('添加成功');
            if (isAddMaterialRef.current) {
                handleAdd(list[0]);
            }
            isAddMaterialRef.current = false;
        },
        onError(msg: string, { file }: any) {
            setImageList((preList) => {
                const index = imageRef.current.findIndex((v) => v.id === file.uid);
                imageRef.current.splice(index, 1, {
                    ...imageRef.current[index],
                    status: 'error',
                    file,
                });
                return [...imageRef.current];
            });
            isAddMaterialRef.current = false;
        },
    };

    /**
     * 清除数据
     */
    const cleanBaseInfo = () => {
        baseInfo.current = {
            targetId: '',
            currentUrl: '',
            isRelease: false,
        };
    };

    const handleAdd = async (item: imgDataType, byMousePosition = false) => {
        editor.tip.showMask({
            loading: true,
            text: '加载中',
        });
        handleAddUploadLayer({
            image: item,
            byMousePosition,
            fitWithStage: true,
            fixedCenter: !byMousePosition,
        });
        cleanBaseInfo();
    };

    const handleChangeStatus = (checked: boolean, type: string) => {
        const temp = imageList.reduce((obj: { [key: number]: boolean }, cur: imgDataType) => {
            !cur?.blobUrl && (obj[+cur.id] = checked);
            return obj;
        }, {});

        setCheckedMap(temp);
    };

    const handleChange = (e: CheckboxChangeEvent, data: imgDataType) => {
        setCheckedMap((pre) => {
            const temp = {
                ...pre,
                [data.id]: e.target.checked,
            };
            return temp;
        });
    };

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };
    const retryHandler = (v: imgDataType) => {
        console.log(v);
        const { file, id, ext } = v;
        const index = imageRef.current.findIndex((v) => v.id === id);
        imageRef.current.splice(index, 1, {
            ...imageRef.current[index],
            status: 'pending',
        });
        setImageList([...imageRef.current]);
        uploadFileToObs(file)
            .then(async (data: any) => {
                const { url } = data;
                const imgInfo = (await getImgInfoAsync(url)) as ImgOnloadProp;
                const {
                    data: {
                        response: { id: _id },
                    },
                } = await saveMyPicture({
                    url,
                    width: imgInfo.width,
                    height: imgInfo.height,
                });
                setImageList((preList) => {
                    imageRef.current.splice(index, 1, {
                        url,
                        id: (_id || '').toString(),
                        width: imgInfo.width,
                        height: imgInfo.height,
                        ext,
                        status: 'success',
                    });
                    return [...imageRef.current];
                });
                // 存入 store 层数据 （目前没有在服务端存储）
                // setMaterialImages(list);
                Message.success('添加成功');
            })
            .catch((error: any) => {
                setImageList((preList) => {
                    imageRef.current.splice(index, 1, {
                        ...imageRef.current[index],
                        status: 'error',
                        file,
                    });
                    return [...imageRef.current];
                });
            });
    };
    const checkedNum = Object.values(checkedMap).filter((item) => item).length;

    const dragStartHandle = (img: imgDataType, ev: React.DragEvent<HTMLDivElement>) => {
        ev.stopPropagation();
        editor.active.delete();
        dragLayerHelper.init(
            {
                naturalWidth: img.width,
                naturalHeight: img.height,
                callback: () => handleAdd(img, true),
            },
            ev,
        );
    };
    const filesUploadAfter = async (data: any) => {
        const { type } = data;
        if (type === 'pending') {
            const { blobUrl, id, name, ext } = data;
            const list = [
                // 预设队首
                {
                    url: '',
                    name,
                    id,
                    blobUrl,
                    width: 0,
                    height: 0,
                    ext,
                },
                ...imageRef.current,
            ];
            setImageList([...list]);
        } else if (type === 'success') {
            const { url, id, _id, width, height, ext } = data;
            setImageList((preList) => {
                const index = imageRef.current.findIndex((v) => v.id === id);
                imageRef.current.splice(index, 1, {
                    url,
                    id: (_id || '').toString(),
                    width,
                    height,
                    ext,
                });
                return [...imageRef.current];
            });
        } else if (type === 'fail') {
            const { file, id } = data;
            setImageList((preList) => {
                const index = imageRef.current.findIndex((v) => v.id === id);
                imageRef.current.splice(index, 1, {
                    ...imageRef.current[index],
                    status: 'error',
                    file,
                });
                return [...imageRef.current];
            });
        }
    };
    useEffect(() => {
        setImageList([]);
        fetchList();
        const onPreventDefaultHandle = (ev: DragEvent) => {
            ev.preventDefault();
        };
        const onDropHandle = async (ev: DragEvent) => {
            ev.preventDefault();
            if (ev.dataTransfer?.files?.length) {
                if (ev.dataTransfer.files.length > 10) {
                    Message.info('仅支持批量上传10张图片');
                }
                const files = Array.from(ev.dataTransfer?.files).slice(0, 10);
                const fileUrls = await filesUpload(files);
                console.log(fileUrls, 'fileUrls===');

                fileUrls.forEach((item) => {
                    saveMyPictureByUrl(item);
                });
                editor.tip.hideMask();
            }
        };
        let imageContainer = document.getElementById('mimeMaterialList');
        imageContainer?.addEventListener('dragover', onPreventDefaultHandle, false);
        imageContainer?.addEventListener('drop', onDropHandle, false);
        events.on(EVENT_FILES_UPLOAD, filesUploadAfter);
        return () => {
            events.off(EVENT_FILES_UPLOAD, filesUploadAfter);
            imageContainer?.removeEventListener('dragover', onPreventDefaultHandle, false);
            imageContainer?.removeEventListener('drop', onDropHandle, false);
            imageContainer = null;
        };
    }, []);
    useEffect(() => {
        imageRef.current = imageList;
    }, [imageList]);

    const isRenderVideo = (item: imgDataType) => {
        try {
            const bizExt = JSON.parse(item.ext || '{}');
            return bizExt.mediaType === MediaType.Video;
        } catch (error) {
            console.warn('Failed to parse video metadata:', error);
            return false;
        }
    };

    return (
        <div className="app-slide-content app-slide-material">
            <div className="border-bottom add-btn image-upload-wrap" ref={uploadRef}>
                {/* 上传图片按钮 */}
                <ImageUpload {...uploadImgProp} tips="仅支持20M以内的图片" />
            </div>
            {checkedNum ? (
                <div className="mine-material__actions">
                    <div className="mine-material__actions__left">
                        <Checkbox
                            checked={checkedNum === imageList.length}
                            onChange={(e) => handleChangeStatus(e.target.checked, 'all')}
                        />
                        <span className="mine-material__actions__left__desc">已选择{checkedNum}条</span>
                        <Divider type="vertical" />
                        <div className="mine-material__actions__left__delete" onClick={() => deleteIndexMaterial()}>
                            {/* <IconFont name="trash" fontSize={20} /> */}
                            <span className="iconfont icon-trash"></span>
                            <span className="mine-material__actions__left__desc">删除</span>
                        </div>
                    </div>
                    <div className="mine-material__actions__cancel" onClick={() => handleChangeStatus(false, 'cancel')}>
                        退出
                    </div>
                </div>
            ) : null}
            <List<imgDataType>
                id="mimeMaterialList"
                className="app-slide-material__list"
                // emptyText="暂无内容，请点击添加图片上传"
                emptyNode={() => {
                    return (
                        <>
                            <IconFont name="EmptyDrawer" fontSize={64} />
                            <p>暂无内容</p>
                            可直接将文件拖至此位置上传或点击添加（仅支持20M以内 jpg、png、heic、svg格式的图片）
                        </>
                    );
                }}
                loading={loading}
                hasMore={hasMore}
                dataSource={imageList}
                nextPageRequest={nextPageRequest}
                isWaterfall
                getItemHeight={(item) => (item.height ? (WidthCard * item.height) / item.width : WidthCard)}
                renderItem={(v) => (
                    <div
                        data-type="track"
                        data-track-id={v.id}
                        key={`${v.id}-${v.url}`}
                        className="item"
                        style={{
                            height: v.height ? (WidthCard / v.width) * v.height : 'auto',
                        }}
                        onClick={() => handleAdd(v)}
                    >
                        {isRenderVideo(v) ? (
                            <div className="app-slide-material__img-play">
                                <IconFont name="MediaPlay" fontSize={30} />
                            </div>
                        ) : (
                            <img
                                draggable="true"
                                onDragEnd={(ev) => {
                                    dragLayerHelper.destroy();
                                }}
                                onDragStart={(ev) => {
                                    dragStartHandle(v, ev);
                                }}
                                src={v.url ? (v.status === 'success' ? v.url : imageConverter(v.url, 320)) : v.blobUrl}
                                alt={v.name}
                            />
                        )}

                        {v.blobUrl ? (
                            <div className="processBox">
                                {v.status === 'error' ? (
                                    <div className="retry">
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                retryHandler(v);
                                            }}
                                        >
                                            重新上传
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="processBar">
                                        <div className="processing"></div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div
                                className={classnames(
                                    'item__operatePanel',
                                    {
                                        'item__operatePanel--active': !!checkedMap[+v.id],
                                    },
                                    {
                                        show: Object.values(checkedMap).some((v) => v),
                                    },
                                )}
                                data-id={v.id}
                            >
                                <Checkbox
                                    className="item__checkbox"
                                    checked={!!checkedMap[+v.id]}
                                    onClick={(e) => handleCheckboxClick(e)}
                                    onChange={(e) => handleChange(e, v)}
                                />
                            </div>
                        )}
                    </div>
                )}
            />
        </div>
    );
}

export default Mine;
